import { gql, useMutation, useQuery } from '@apollo/client';
import * as React from 'react';

export enum EInAppNotificationType {
  GroupAccessPolicyPaused = 'GROUP_ACCESS_POLICY_PAUSED',
  GroupAccessPolicyRestored = 'GROUP_ACCESS_POLICY_RESTORED',
}

export type IInAppSystemNotification = {
  _id: string;
  type: EInAppNotificationType;
  body: string;
  groupId?: string | null;
  groupName?: string | null;
  isRead: boolean;
  isSeen: boolean;
  dateCreated?: string | null;
};

const MY_IN_APP_NOTIFICATIONS_QUERY = gql`
  query MyInAppNotifications_Query($input: MyInAppNotificationsInput) {
    myInAppNotifications(input: $input) {
      results {
        _id
        type
        body
        groupId
        groupName
        isRead
        isSeen
        dateCreated
      }
      totalCount
      unseenCount
    }
  }
`;

const MARK_IN_APP_NOTIFICATION_READ = gql`
  mutation MyInAppNotifications_MarkRead(
    $input: MarkInAppNotificationReadInput!
  ) {
    markInAppNotificationRead(input: $input) {
      notification {
        _id
        isRead
        isSeen
      }
    }
  }
`;

const MARK_IN_APP_NOTIFICATIONS_SEEN = gql`
  mutation MyInAppNotifications_MarkSeen {
    markInAppNotificationsSeen {
      success
    }
  }
`;

type IUseInAppSystemNotificationsResult = {
  notifications: IInAppSystemNotification[];
  unseenCount: number;
  loading: boolean;
  hasMore: boolean;
  fetchMore: () => void;
  markRead: (notificationId: string) => Promise<void>;
  markAllSeen: () => Promise<void>;
};

export function useInAppSystemNotifications(options: {
  limit: number;
}): IUseInAppSystemNotificationsResult {
  const { limit } = options;

  const { data, loading, fetchMore } = useQuery(MY_IN_APP_NOTIFICATIONS_QUERY, {
    variables: { input: { limit, offset: 0 } },
    pollInterval: 30000,
  });

  const [markReadMutation] = useMutation(MARK_IN_APP_NOTIFICATION_READ, {
    refetchQueries: ['MyInAppNotifications_Query'],
  });
  const [markSeenMutation] = useMutation(MARK_IN_APP_NOTIFICATIONS_SEEN, {
    refetchQueries: ['MyInAppNotifications_Query'],
  });

  const page = data?.myInAppNotifications;
  const notifications: IInAppSystemNotification[] = page?.results || [];
  const totalCount: number = page?.totalCount || 0;
  const unseenCount: number = page?.unseenCount || 0;

  const fetchMoreNotifications = React.useCallback(() => {
    fetchMore({
      variables: {
        input: { limit, offset: notifications.length },
      },
      updateQuery: (previousResult: any, { fetchMoreResult }: any) => {
        if (!fetchMoreResult?.myInAppNotifications) {
          return previousResult;
        }
        return {
          myInAppNotifications: {
            ...fetchMoreResult.myInAppNotifications,
            results: [
              ...(previousResult.myInAppNotifications?.results || []),
              ...fetchMoreResult.myInAppNotifications.results,
            ],
          },
        };
      },
    });
  }, [fetchMore, limit, notifications.length]);

  const markRead = React.useCallback(
    async (notificationId: string) => {
      await markReadMutation({
        variables: { input: { notificationId } },
      });
    },
    [markReadMutation]
  );

  const markAllSeen = React.useCallback(async () => {
    await markSeenMutation();
  }, [markSeenMutation]);

  return {
    notifications,
    unseenCount,
    loading,
    hasMore: notifications.length < totalCount,
    fetchMore: fetchMoreNotifications,
    markRead,
    markAllSeen,
  };
}
